import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  AudioLines, Plus, X, Play, Pause, Download, Mic,
  Image as ImageIcon, Sparkles, Trash2, Loader2, Upload, Wand2,
} from 'lucide-react';
import './VoicesPage.css';
import { useAuthContext } from './components/AuthProvider';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import {
  generateVoiceTts,
  generateSeedVoicePreview,
  saveSeedVoice,
  deleteMyVoice,
} from './lib/api';
import { useVoiceLibraryData, useVoiceLibraryStore, fetchVoiceLibraryIfStale } from './stores';

const SEARCH_DEBOUNCE_MS = 300;
const MAX_REFERENCE_AUDIO = 3;
const MAX_TTS_CHARS = 2500;

/* Deterministic gradient per voice so every voice gets a stable identity color */
const ORB_GRADIENTS = [
  ['#f5c99b', '#c07b42'],
  ['#a8c6e8', '#4a6fa5'],
  ['#c5b3e6', '#7c5cbf'],
  ['#a9d8b8', '#3f8f5f'],
  ['#f2b3c1', '#c0526f'],
  ['#e8d9a8', '#a68a3d'],
  ['#b3d9e0', '#3d8a99'],
  ['#e6b8a2', '#b05c38'],
];

function orbGradient(seed) {
  let hash = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0; // eslint-disable-line no-bitwise
  }
  const [from, to] = ORB_GRADIENTS[hash % ORB_GRADIENTS.length];
  return `radial-gradient(circle at 30% 30%, ${from}, ${to})`;
}

function formatSeconds(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/* Small animated equalizer shown while audio is playing */
function Equalizer() {
  return (
    <span className="voices-equalizer" aria-hidden="true">
      <span /><span /><span /><span />
    </span>
  );
}

/* ==========================================================================
   Audio player — shared minimal player with progress bar + download
   ========================================================================== */
function AudioPlayer({ src, downloadName, compact = false }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, []);

  const handleSeek = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * audio.duration;
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`voices-player ${compact ? 'voices-player--compact' : ''}`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
      <button type="button" className="voices-player-play" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
        {isPlaying ? <Pause size={15} /> : <Play size={15} style={{ marginLeft: 1 }} />}
      </button>
      <div className="voices-player-track" onClick={handleSeek} role="presentation">
        <div className="voices-player-progress" style={{ width: `${progress}%` }} />
      </div>
      <span className="voices-player-time">
        {formatSeconds(currentTime)} / {formatSeconds(duration)}
      </span>
      <a className="voices-player-download" href={src} download={downloadName || 'audio.mp3'} target="_blank" rel="noreferrer" aria-label="Download audio">
        <Download size={14} />
      </a>
    </div>
  );
}

/* ==========================================================================
   Voice card
   ========================================================================== */
function VoiceCard({ voice, isPlaying, onTogglePreview, onOpen, onDelete }) {
  const description = voice.description || '';

  return (
    <div
      className="voice-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
    >
      <div className="voice-card-top">
        <div className="voice-card-orb" style={{ background: orbGradient(voice.id) }}>
          {voice.previewUrl && (
            <button
              type="button"
              className={`voice-card-play ${isPlaying ? 'playing' : ''}`}
              onClick={(e) => { e.stopPropagation(); onTogglePreview(); }}
              aria-label={isPlaying ? 'Stop preview' : 'Play preview'}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: 2 }} />}
            </button>
          )}
        </div>
        <div className="voice-card-heading">
          <span className="voice-card-name" title={voice.name}>{voice.name}</span>
        </div>
        {isPlaying && <Equalizer />}
        <button
          type="button"
          className="voice-card-delete"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          aria-label="Delete voice"
        >
          <Trash2 size={13} />
        </button>
      </div>
      {description && <p className="voice-card-description">{description}</p>}
      <div className="voice-card-footer">
        <span className="voice-card-cta">
          <Mic size={12} />
          Generate speech
        </span>
      </div>
    </div>
  );
}

/* ==========================================================================
   TTS Studio — modal to generate speech with a saved voice
   ========================================================================== */
function TtsStudioModal({ voice, onClose }) {
  const [text, setText] = useState('');
  const [speed, setSpeed] = useState(1.0);
  const [pitch, setPitch] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [canExpandDescription, setCanExpandDescription] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generations, setGenerations] = useState([]); // newest first
  const previewAudioRef = useRef(null);
  const descriptionRef = useRef(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (isDescriptionExpanded) return undefined;

    const description = descriptionRef.current;
    if (!description) {
      setCanExpandDescription(false);
      return undefined;
    }

    const measureOverflow = () => {
      setCanExpandDescription(description.scrollHeight > description.clientHeight + 1);
    };

    measureOverflow();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measureOverflow);
    observer.observe(description);
    return () => observer.disconnect();
  }, [voice.description, isDescriptionExpanded]);

  const togglePreview = useCallback(() => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || isGenerating) return;
    try {
      setIsGenerating(true);
      setError(null);
      const options = {};
      if (speed !== 1.0) options.speed = speed;
      if (pitch !== 0) options.pitch = pitch;
      const result = await generateVoiceTts(voice.id, trimmed, options);
      setGenerations((prev) => [
        { id: `${Date.now()}`, text: trimmed, audioUrl: result.audioUrl, duration: result.duration },
        ...prev,
      ]);
    } catch (err) {
      setError('Failed to generate speech');
    } finally {
      setIsGenerating(false);
    }
  }, [text, speed, pitch, isGenerating, voice.id]);

  const latest = generations[0];
  const history = generations.slice(1);

  return (
    <div className="voices-modal-overlay" onClick={onClose}>
      <div className="voices-studio" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="voices-studio-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>

        {/* Voice identity panel */}
        <aside className="voices-studio-identity">
          <div className="voices-studio-orb" style={{ background: orbGradient(voice.id) }}>
            {voice.previewUrl && (
              <button
                type="button"
                className={`voices-studio-orb-play ${previewPlaying ? 'playing' : ''}`}
                onClick={togglePreview}
                aria-label={previewPlaying ? 'Stop preview' : 'Play preview'}
              >
                {previewPlaying ? <Pause size={20} /> : <Play size={20} style={{ marginLeft: 2 }} />}
              </button>
            )}
          </div>
          {voice.previewUrl && (
            <audio
              ref={previewAudioRef}
              src={voice.previewUrl}
              preload="none"
              onPlay={() => setPreviewPlaying(true)}
              onPause={() => setPreviewPlaying(false)}
              onEnded={() => setPreviewPlaying(false)}
            />
          )}
          <h2 className="voices-studio-name">{voice.name}</h2>
          {voice.description && (
            <div className="voices-studio-description-wrap">
              <p
                ref={descriptionRef}
                id="voices-studio-description"
                className={`voices-studio-description ${isDescriptionExpanded ? 'is-expanded' : ''}`}
              >
                {voice.description}
              </p>
              {canExpandDescription && (
                <button
                  type="button"
                  className="voices-studio-description-toggle"
                  aria-controls="voices-studio-description"
                  aria-expanded={isDescriptionExpanded}
                  onClick={() => setIsDescriptionExpanded((expanded) => !expanded)}
                >
                  {isDescriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}
          {voice.previewUrl && (
            <span className="voices-studio-preview-hint">
              {previewPlaying ? 'Playing voice preview…' : 'Tap the orb to hear this voice'}
            </span>
          )}
        </aside>

        {/* TTS workspace */}
        <div className="voices-studio-main">
          <div className="voices-studio-header">
            <h3 className="voices-studio-title">Text to Speech</h3>
            <p className="voices-studio-subtitle">Write anything and hear it in this voice.</p>
          </div>

          <div className="voices-studio-input-wrap">
            <textarea
              className="voices-studio-textarea"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_TTS_CHARS))}
              placeholder={`What should ${voice.name} say?`}
              rows={5}
              autoFocus
            />
            <span className="voices-studio-charcount">{text.length} / {MAX_TTS_CHARS}</span>
          </div>

          <div className="voices-studio-controls">
            <label className="voices-studio-slider">
              <span className="voices-studio-slider-label">
                Speed <strong>{speed.toFixed(2)}×</strong>
              </span>
              <input
                type="range" min="0.5" max="2" step="0.05"
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
              />
            </label>
            <label className="voices-studio-slider">
              <span className="voices-studio-slider-label">
                Pitch <strong>{pitch > 0 ? `+${pitch}` : pitch} st</strong>
              </span>
              <input
                type="range" min="-12" max="12" step="1"
                value={pitch}
                onChange={(e) => setPitch(parseInt(e.target.value, 10))}
              />
            </label>
            <button
              type="button"
              className="voices-generate-button"
              onClick={handleGenerate}
              disabled={!text.trim() || isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 size={14} className="voices-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  Generate Speech
                </>
              )}
            </button>
          </div>

          {error && <div className="voices-error">{error}</div>}

          {latest && (
            <div className="voices-studio-result">
              <span className="voices-studio-result-label">Latest generation</span>
              <AudioPlayer src={latest.audioUrl} downloadName={`${voice.name}-tts.mp3`} />
              <p className="voices-studio-result-text">“{latest.text}”</p>
            </div>
          )}

          {history.length > 0 && (
            <div className="voices-studio-history">
              <span className="voices-studio-result-label">Earlier in this session</span>
              {history.map((g) => (
                <div key={g.id} className="voices-studio-history-item">
                  <AudioPlayer src={g.audioUrl} downloadName={`${voice.name}-tts.mp3`} compact />
                  <p className="voices-studio-history-text">“{g.text}”</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   Voice Creation Studio — design a new voice from scratch with Seed Audio
   ========================================================================== */
function CreateVoiceStudioModal({ onClose, onSaved }) {
  const [description, setDescription] = useState('');
  const [sampleText, setSampleText] = useState('');
  const [referenceMode, setReferenceMode] = useState('none'); // none | audio | image
  const [audioFiles, setAudioFiles] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [preview, setPreview] = useState(null); // { previewUrl, duration }
  const [voiceName, setVoiceName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => () => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
  }, [imagePreviewUrl]);

  const handleAudioFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setAudioFiles((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= MAX_REFERENCE_AUDIO) break;
        next.push(file);
      }
      return next;
    });
  }, []);

  const handleImageFile = useCallback((file) => {
    if (!file) return;
    setImageFile(file);
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  }, []);

  const switchMode = useCallback((mode) => {
    setReferenceMode(mode);
    setPreview(null);
    if (mode !== 'audio') setAudioFiles([]);
    if (mode !== 'image') {
      setImageFile(null);
      setImagePreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, []);

  const buildPrompt = useCallback(() => {
    const parts = [];
    const desc = description.trim();
    if (referenceMode === 'audio' && audioFiles.length > 0) {
      const tags = audioFiles.map((_, i) => `@Audio${i + 1}`).join(', ');
      parts.push(`The speaker's voice matches the reference audio ${tags}.`);
    }
    if (referenceMode === 'image' && imageFile) {
      parts.push("The speaker's voice matches the character in the reference image.");
    }
    if (desc) parts.push(desc);
    const say = sampleText.trim();
    parts.push(`Say the following: "${say}"`);
    return parts.join(' ');
  }, [description, sampleText, referenceMode, audioFiles, imageFile]);

  const canGenerate = sampleText.trim().length > 0 && (
    description.trim().length > 0 ||
    (referenceMode === 'audio' && audioFiles.length > 0) ||
    (referenceMode === 'image' && !!imageFile)
  );

  const handleGenerate = useCallback(async () => {
    if (!canGenerate || isGenerating) return;
    try {
      setIsGenerating(true);
      setError(null);
      setPreview(null);
      const result = await generateSeedVoicePreview({
        prompt: buildPrompt(),
        audioFiles: referenceMode === 'audio' ? audioFiles : [],
        imageFile: referenceMode === 'image' ? imageFile : null,
      });
      setPreview({ previewUrl: result.previewUrl, duration: result.duration });
    } catch (err) {
      setError('Failed to generate voice preview');
    } finally {
      setIsGenerating(false);
    }
  }, [canGenerate, isGenerating, buildPrompt, referenceMode, audioFiles, imageFile]);

  const handleSave = useCallback(async () => {
    if (!preview || !voiceName.trim() || isSaving) return;
    try {
      setIsSaving(true);
      setError(null);
      const result = await saveSeedVoice({
        name: voiceName.trim(),
        description: description.trim() || sampleText.trim(),
        previewUrl: preview.previewUrl,
      });
      if (result.voice) {
        onSaved(result.voice);
        onClose();
      }
    } catch (err) {
      setError('Failed to save voice');
    } finally {
      setIsSaving(false);
    }
  }, [preview, voiceName, isSaving, description, sampleText, onSaved, onClose]);

  return (
    <div className="voices-modal-overlay" onClick={onClose}>
      <div className="voices-create-studio" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="voices-studio-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>

        <div className="voices-create-header">
          <div className="voices-create-header-icon">
            <Wand2 size={17} />
          </div>
          <div>
            <h2 className="voices-create-title">Design a Voice</h2>
            <p className="voices-create-subtitle">
              Describe a voice — or steer it with reference audio or an image — and hear it instantly.
            </p>
          </div>
        </div>

        <div className="voices-create-body">
          <label className="voices-create-label">
            Voice description
            <textarea
              className="voices-create-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. A warm, gravelly storyteller in his 60s with a slow, deliberate cadence and a faint Irish accent"
              rows={3}
            />
          </label>

          {/* Reference selector */}
          <div className="voices-create-refs">
            <span className="voices-create-refs-label">Reference</span>
            <div className="voices-create-refs-tabs">
              <button
                type="button"
                className={`voices-refs-tab ${referenceMode === 'none' ? 'active' : ''}`}
                onClick={() => switchMode('none')}
              >
                <Sparkles size={12} />
                Description only
              </button>
              <button
                type="button"
                className={`voices-refs-tab ${referenceMode === 'audio' ? 'active' : ''}`}
                onClick={() => switchMode('audio')}
              >
                <AudioLines size={12} />
                Audio clips
              </button>
              <button
                type="button"
                className={`voices-refs-tab ${referenceMode === 'image' ? 'active' : ''}`}
                onClick={() => switchMode('image')}
              >
                <ImageIcon size={12} />
                Image
              </button>
            </div>

            {referenceMode === 'audio' && (
              <div className="voices-refs-panel">
                <p className="voices-refs-hint">
                  Up to {MAX_REFERENCE_AUDIO} clips (30s / 10MB each). The generated voice will imitate them.
                </p>
                <div className="voices-refs-audio-list">
                  {audioFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="voices-refs-audio-chip">
                      <AudioLines size={12} />
                      <span className="voices-refs-audio-name" title={file.name}>@Audio{index + 1} · {file.name}</span>
                      <button
                        type="button"
                        className="voices-refs-audio-remove"
                        onClick={() => setAudioFiles((prev) => prev.filter((_, i) => i !== index))}
                        aria-label={`Remove ${file.name}`}
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                  {audioFiles.length < MAX_REFERENCE_AUDIO && (
                    <button type="button" className="voices-refs-add" onClick={() => audioInputRef.current?.click()}>
                      <Upload size={12} />
                      Add audio
                    </button>
                  )}
                </div>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept=".wav,.mp3,.ogg,.m4a,audio/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => { handleAudioFiles(e.target.files); e.target.value = ''; }}
                />
              </div>
            )}

            {referenceMode === 'image' && (
              <div className="voices-refs-panel">
                <p className="voices-refs-hint">
                  One image (jpeg/png/webp). A voice matching the character will be created.
                </p>
                {imageFile ? (
                  <div className="voices-refs-image-preview">
                    <img src={imagePreviewUrl} alt="Voice reference" />
                    <button
                      type="button"
                      className="voices-refs-image-remove"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
                      }}
                      aria-label="Remove image"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button type="button" className="voices-refs-add" onClick={() => imageInputRef.current?.click()}>
                    <Upload size={12} />
                    Choose image
                  </button>
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={(e) => { handleImageFile(e.target.files?.[0]); e.target.value = ''; }}
                />
              </div>
            )}
          </div>

          <label className="voices-create-label">
            What should it say?
            <textarea
              className="voices-create-textarea"
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              placeholder="This line is spoken in the new voice and becomes its preview audio."
              rows={2}
            />
          </label>

          <button
            type="button"
            className="voices-generate-button voices-generate-button--wide"
            onClick={handleGenerate}
            disabled={!canGenerate || isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 size={14} className="voices-spin" />
                Designing voice… this can take a moment
              </>
            ) : (
              <>
                <Sparkles size={14} />
                {preview ? 'Regenerate Voice' : 'Generate Voice'}
              </>
            )}
          </button>

          {error && <div className="voices-error">{error}</div>}

          {preview && (
            <div className="voices-create-result">
              <span className="voices-studio-result-label">Voice preview</span>
              <AudioPlayer src={preview.previewUrl} downloadName="voice-preview.mp3" />
              <div className="voices-create-save-row">
                <input
                  type="text"
                  className="voices-create-name-input"
                  value={voiceName}
                  onChange={(e) => setVoiceName(e.target.value)}
                  placeholder="Name this voice…"
                  maxLength={120}
                />
                <button
                  type="button"
                  className="voices-save-button"
                  onClick={handleSave}
                  disabled={!voiceName.trim() || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={14} className="voices-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Plus size={14} />
                      Save Voice
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   Page
   ========================================================================== */
function VoicesPage() {
  const { user, loading } = useAuthContext();
  const { items: voices, isLoading, error: cacheError, hasCache } = useVoiceLibraryData(user?.id);

  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const previewAudioRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery]);

  useEffect(() => {
    if (loading || !user) return;
    fetchVoiceLibraryIfStale({ userId: user.id });
  }, [user, loading]);

  useEffect(() => {
    if (cacheError) setError(cacheError);
  }, [cacheError]);

  // Stop preview audio when unmounting
  useEffect(() => () => {
    previewAudioRef.current?.pause();
  }, []);

  const togglePreview = useCallback((voice) => {
    if (!voice.previewUrl) return;
    const current = previewAudioRef.current;
    if (playingVoiceId === voice.id && current) {
      current.pause();
      previewAudioRef.current = null;
      setPlayingVoiceId(null);
      return;
    }
    current?.pause();
    const audio = new Audio(voice.previewUrl);
    audio.onended = () => setPlayingVoiceId((prev) => (prev === voice.id ? null : prev));
    audio.onerror = () => setPlayingVoiceId((prev) => (prev === voice.id ? null : prev));
    previewAudioRef.current = audio;
    setPlayingVoiceId(voice.id);
    audio.play().catch(() => setPlayingVoiceId(null));
  }, [playingVoiceId]);

  const handleDelete = useCallback(async (voice) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the voice "${voice.name}"? This cannot be undone.`)) return;
    try {
      await deleteMyVoice(voice.id);
      useVoiceLibraryStore.getState().removeLibraryVoice(voice.id);
      if (playingVoiceId === voice.id) {
        previewAudioRef.current?.pause();
        setPlayingVoiceId(null);
      }
    } catch (err) {
      setError('Failed to delete voice');
    }
  }, [playingVoiceId]);

  const filteredVoices = useMemo(() => {
    if (!debouncedSearch.trim()) return voices;
    const q = debouncedSearch.toLowerCase();
    return voices.filter((v) =>
      v.name?.toLowerCase().includes(q) ||
      v.description?.toLowerCase().includes(q) ||
      v.character?.name?.toLowerCase().includes(q)
    );
  }, [voices, debouncedSearch]);

  return (
    <AuthenticatedLayout>
      <div className="voices-page-content">
        <div className="voices-page-header-section">
          <div className="voices-page-header-left">
            <h1 className="voices-page-title">Voices</h1>
            <p className="voices-page-subtitle">
              Every voice you've generated — click one to turn text into speech
            </p>
          </div>
          <button className="voices-page-new-button" onClick={() => setShowCreate(true)}>
            <Wand2 size={14} />
            Design Voice
          </button>
        </div>

        <div className="voices-page-search-row">
          <input
            type="text"
            className="voices-page-search"
            placeholder="Search voices…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="voices-page-scroll">
          {error && <div className="voices-error">{error}</div>}
          {isLoading && !hasCache && <div className="voices-page-loading">Loading voices…</div>}

          {hasCache && !error && filteredVoices.length === 0 && (
            <div className="voices-page-empty">
              <AudioLines size={40} className="voices-page-empty-icon" />
              {debouncedSearch ? (
                <p>No voices match your search.</p>
              ) : (
                <>
                  <p>No voices yet.</p>
                  <p className="voices-page-empty-hint">
                    Design your first voice from a description, reference audio, or an image.
                  </p>
                  <button className="voices-page-empty-cta" onClick={() => setShowCreate(true)}>
                    <Wand2 size={14} />
                    Design your first voice
                  </button>
                </>
              )}
            </div>
          )}

          {filteredVoices.length > 0 && (
            <div className="voices-page-grid">
              {filteredVoices.map((voice) => (
                <VoiceCard
                  key={voice.id}
                  voice={voice}
                  isPlaying={playingVoiceId === voice.id}
                  onTogglePreview={() => togglePreview(voice)}
                  onOpen={() => setSelectedVoice(voice)}
                  onDelete={() => handleDelete(voice)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedVoice && (
        <TtsStudioModal voice={selectedVoice} onClose={() => setSelectedVoice(null)} />
      )}

      {showCreate && (
        <CreateVoiceStudioModal
          onClose={() => setShowCreate(false)}
          onSaved={(voice) => useVoiceLibraryStore.getState().addLibraryVoice({ ...voice, character: null })}
        />
      )}
    </AuthenticatedLayout>
  );
}

export default VoicesPage;
